import { InputTextarea } from "primereact/inputtextarea";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as client from "../../client";
import { displayNameForUser } from "../../util";
import { PantheonAvatarWithoutMenu } from "../avatar/Avatar";
import {
  ActiveMention,
  filterMentionableUsers,
  getActiveMention,
  insertMention,
} from "./mentions";
import styles from "./MentionInput.module.css";

type MentionInputProps = {
  value: string;
  onChange: (value: string) => void;
  // Users offered in the suggestion dropdown (the caller's org).
  users: client.User[];
  // Typically the current user, who has no reason to mention themselves.
  excludeUserId: string | undefined;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  autoResize?: boolean;
  style?: React.CSSProperties;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
};

const PANEL_MAX_HEIGHT_PX = 240;

// A textarea with Slack-style @-mentions: typing "@" opens a dropdown of org
// users filtered as you type; arrow keys/Enter/Tab or a click insert the
// canonical "@[Display Name](user_id)" token.
//
// The dropdown is portaled to document.body with fixed positioning so it can
// never be clipped by scrollable ancestors (the comment overlay scrolls).
export const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  users,
  excludeUserId,
  placeholder,
  disabled,
  rows,
  autoResize,
  style,
  onKeyDown,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [activeMention, setActiveMention] = useState<ActiveMention | null>(
    null
  );
  const [highlightIndex, setHighlightIndex] = useState(0);

  // If the value changes out from under us (e.g. cleared after submit),
  // drop a stale active mention.
  useEffect(() => {
    if (activeMention && value[activeMention.start] !== "@") {
      setActiveMention(null);
    }
  }, [value, activeMention]);

  const suggestions = activeMention
    ? filterMentionableUsers(users, activeMention.query, excludeUserId)
    : [];
  const open = activeMention !== null && suggestions.length > 0;
  const highlight = Math.min(highlightIndex, suggestions.length - 1);

  const updateActiveMention = (target: HTMLTextAreaElement) => {
    const mention = getActiveMention(target.value, target.selectionStart);
    if (
      mention?.start !== activeMention?.start ||
      mention?.query !== activeMention?.query
    ) {
      setActiveMention(mention);
      setHighlightIndex(0);
    }
  };

  const select = (user: client.User) => {
    const textarea = textareaRef.current;
    if (!activeMention || !textarea) {
      return;
    }
    const next = insertMention(
      value,
      activeMention,
      textarea.selectionStart,
      user
    );
    onChange(next.value);
    setActiveMention(null);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(next.caret, next.caret);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (open) {
      const highlighted = suggestions[highlight];
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex((highlight + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex(
          (highlight - 1 + suggestions.length) % suggestions.length
        );
        return;
      }
      if (
        highlighted &&
        (e.key === "Tab" ||
          (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey))
      ) {
        e.preventDefault();
        select(highlighted);
        return;
      }
      if (e.key === "Escape") {
        // Only close the dropdown; don't let Escape bubble into handlers
        // that would close the surrounding editor.
        e.preventDefault();
        e.stopPropagation();
        setActiveMention(null);
        return;
      }
    }
    onKeyDown?.(e);
  };

  // Anchor the dropdown to the textarea, flipping above it when there is not
  // enough room below. Recomputed every render while open, which covers the
  // textarea growing as the user types.
  const anchorRect = open
    ? textareaRef.current?.getBoundingClientRect()
    : undefined;
  const flipUp = anchorRect
    ? window.innerHeight - anchorRect.bottom < PANEL_MAX_HEIGHT_PX + 8
    : false;
  const panelStyle: React.CSSProperties | undefined = anchorRect
    ? {
        position: "fixed",
        left: anchorRect.left,
        width: Math.max(anchorRect.width, 280),
        zIndex: 10000,
        ...(flipUp
          ? { bottom: window.innerHeight - anchorRect.top + 4 }
          : { top: anchorRect.bottom + 4 }),
      }
    : undefined;

  return (
    <>
      <InputTextarea
        ref={textareaRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          updateActiveMention(e.target);
        }}
        onSelect={(e) => updateActiveMention(e.currentTarget)}
        onKeyDown={handleKeyDown}
        onBlur={() => setActiveMention(null)}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        autoResize={autoResize}
        style={style}
      />
      {open &&
        panelStyle &&
        createPortal(
          <div
            className={styles.panel}
            style={panelStyle}
            onClick={(e) => e.stopPropagation()}
          >
            {suggestions.map((user, index) => (
              <div
                key={user.id}
                className={
                  index === highlight
                    ? `${styles.item} ${styles.itemActive}`
                    : styles.item
                }
                // Keep focus in the textarea so selecting doesn't blur-close
                // the dropdown before the click lands.
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  select(user);
                }}
                onMouseEnter={() => setHighlightIndex(index)}
              >
                <PantheonAvatarWithoutMenu
                  label={displayNameForUser(user)}
                  imageUrl={user.profile_image_url}
                  showName={true}
                />
                <span className={styles.itemEmail}>{user.email}</span>
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};
